/**
 * DisabledPaymentProvider — PAYMENT_MODE = 'disabled'.
 *
 * Passes the payment states without any payment:
 *   beginPayment    creates a local order (dev_…), status PENDING, bypassed=true
 *   verifyPayment   marks that order CONFIRMED, authority 'disabled-bypass'
 *   issueEntitlement local temporary entitlement, issuedBy 'local-disabled'
 *
 * Everything is idempotent per quoteId / orderId: a second beginPayment for the
 * same quote returns the same PaymentResult (never a second order), a second
 * verifyPayment returns the same confirmed result, a second issueEntitlement
 * the same entitlement. Nothing is persisted; a page reload starts empty.
 *
 * What it never does: pretend money moved (bypassed stays true), invent a
 * PAYUNi transaction id, or issue an entitlement for a non-CONFIRMED payment.
 */

import { DISABLED_ORDER_ID_PREFIX } from './config';
import type { PaymentProvider, PaymentRequest, PaymentResult, TranslationEntitlement } from './types';

export interface DisabledProviderOptions {
  /** Clock override for tests (ms since epoch). */
  now?: () => number;
  /** Order id suffix generator override for tests. */
  orderSuffix?: () => string;
}

function randomSuffix(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID().replace(/-/g, '').slice(0, 16);
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

export class DisabledPaymentProvider implements PaymentProvider {
  readonly mode = 'disabled' as const;
  private readonly now: () => number;
  private readonly orderSuffix: () => string;
  private readonly byQuote = new Map<string, PaymentResult>();
  private readonly byOrder = new Map<string, PaymentResult>();
  private readonly entitlements = new Map<string, TranslationEntitlement>();

  constructor(options: DisabledProviderOptions = {}) {
    this.now = options.now ?? Date.now;
    this.orderSuffix = options.orderSuffix ?? randomSuffix;
  }

  async beginPayment(request: PaymentRequest): Promise<PaymentResult> {
    const existing = this.byQuote.get(request.quoteId);
    if (existing) return existing;
    if (!(request.amount >= 0) || !Number.isFinite(request.amount)) throw new Error(`payment: invalid amount ${request.amount}`);
    const result: PaymentResult = {
      paymentMode: 'disabled',
      quoteId: request.quoteId,
      orderId: `${DISABLED_ORDER_ID_PREFIX}${this.orderSuffix()}`,
      amount: request.amount,
      currency: 'TWD',
      status: 'PENDING',
      createdAt: new Date(this.now()).toISOString(),
      confirmedAt: null,
      bypassed: true,
      authority: 'disabled-bypass',
    };
    this.byQuote.set(request.quoteId, result);
    this.byOrder.set(result.orderId, result);
    return result;
  }

  async verifyPayment(orderId: string): Promise<PaymentResult> {
    const order = this.byOrder.get(orderId);
    if (!order) throw new Error(`payment: unknown order ${orderId}`);
    if (order.status === 'CONFIRMED') return order;
    if (order.status !== 'PENDING') return order; // FAILED / CANCELLED stay as they are
    const confirmed: PaymentResult = { ...order, status: 'CONFIRMED', confirmedAt: new Date(this.now()).toISOString() };
    this.byOrder.set(orderId, confirmed);
    this.byQuote.set(order.quoteId, confirmed);
    return confirmed;
  }

  async issueEntitlement(payment: PaymentResult, request: PaymentRequest): Promise<TranslationEntitlement> {
    if (payment.status !== 'CONFIRMED') throw new Error(`payment: no entitlement for a ${payment.status} payment`);
    const known = this.byOrder.get(payment.orderId);
    if (!known || known.status !== 'CONFIRMED' || known.quoteId !== request.quoteId) throw new Error('payment: entitlement requested for an order this provider did not confirm');
    const existing = this.entitlements.get(payment.orderId);
    if (existing) return existing;
    const entitlement: TranslationEntitlement = {
      quoteId: request.quoteId,
      orderId: payment.orderId,
      documentFingerprint: request.documentFingerprint,
      scopeFingerprint: request.scopeFingerprint,
      quotedAmount: request.amount,
      status: 'ACTIVE',
      issuedAt: new Date(this.now()).toISOString(),
      expiresAt: request.quoteExpiresAt, // a local entitlement lives exactly as long as its quote
      issuedBy: 'local-disabled',
    };
    this.entitlements.set(payment.orderId, entitlement);
    return entitlement;
  }

  /** Tests / Developer Mode: how many orders exist (never more than one per quote). */
  orderCount(): number {
    return this.byOrder.size;
  }
}
